(function () {
	'use strict';

	/**
	 * Destroy the carousel instance attached to a single carousel element.
	 * Removes all event listeners, clears the autoplay timer, and resets the
	 * initialized flag so the carousel can be cleanly re-initialized.
	 *
	 * @param {Element} carousel
	 */
	function destroyCarousel(carousel) {
		var state = carousel.__rfCarouselState;
		if (!state) { return; }

		// Stop autoplay timer
		state.stopAutoplay();

		// Remove all carousel-element listeners
		state.listeners.forEach(function (entry) {
			entry.target.removeEventListener(entry.type, entry.fn, entry.options || false);
		});

		// Remove window-level resize listener
		if (state.windowResizeHandler) {
			window.removeEventListener('resize', state.windowResizeHandler);
			state.windowResizeHandler = null;
		}

		// Reset state
		carousel.__rfCarouselState = null;
		delete carousel.dataset.carouselInitialized;
	}

	window.ReviewFlowCarousel = {
		initAll: function (container) {
			var scope = container || document;
			var carousels = scope.querySelectorAll('.reviewflow-carousel');
			carousels.forEach(function (carousel) {
				if (carousel.dataset.carouselInitialized !== '1') {
					carousel.dataset.carouselInitialized = '1';
					initCarousel(carousel);
				}
			});
		},

		/**
		 * Destroy all carousel instances inside `root` (or the full document).
		 * Called by CarouselLayoutEngine.destroy(root) in layout-engine-manager.js.
		 *
		 * @param {Element|Document} [root]
		 */
		destroyAll: function (root) {
			var scope = root || document;
			scope.querySelectorAll('.reviewflow-carousel').forEach(destroyCarousel);
		}
	};

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', function () {
			window.ReviewFlowCarousel.initAll();
		});
	} else {
		window.ReviewFlowCarousel.initAll();
	}

	function initCarousel(carousel) {
		var track       = carousel.querySelector('.reviewflow-carousel__track');
		var slides      = Array.from(carousel.querySelectorAll('.reviewflow-carousel__slide'));
		var nextButton  = carousel.querySelector('.reviewflow-carousel__button--next');
		var prevButton  = carousel.querySelector('.reviewflow-carousel__button--prev');
		var indicators  = Array.from(carousel.querySelectorAll('.reviewflow-carousel__indicator'));

		if (slides.length === 0) { return; }

		var currentIndex     = 0;
		var hasReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		var autoplay         = !hasReducedMotion && (carousel.getAttribute('data-autoplay') === 'true');
		var interval         = parseInt(carousel.getAttribute('data-interval'), 10) || 5000;
		var loop             = carousel.getAttribute('data-loop') !== 'false';
		var draggable        = carousel.getAttribute('data-draggable') !== 'false';
		var transitionSpeed  = parseInt(carousel.getAttribute('data-transition-speed'), 10);
		if (isNaN(transitionSpeed) || transitionSpeed < 0) {
			transitionSpeed = 300;
		}
		if (track) {
			track.style.transition = 'transform ' + transitionSpeed + 'ms cubic-bezier(0.4, 0, 0.2, 1)';
		}
		var autoplayTimer    = null;
		var isExpandedPaused = false;

		function hasExpandedReviews() {
			return carousel.querySelector('[data-reviewflow-read-more][aria-expanded="true"]') !== null;
		}

		function collapseAllInCarousel(animate) {
			if (window.reviewflowCollapseAll) {
				window.reviewflowCollapseAll(carousel, animate);
			} else {
				var buttons = carousel.querySelectorAll('[data-reviewflow-read-more][aria-expanded="true"]');
				buttons.forEach(function (btn) {
					btn.click();
				});
			}
		}

		// ── State object stored on the element for destroyCarousel() ─────────
		// listeners: { target, type, fn, options? }[]
		// windowResizeHandler: fn | null
		var state = {
			listeners:           [],
			windowResizeHandler: null,
			stopAutoplay:        stopAutoplay
		};
		carousel.__rfCarouselState = state;

		/** Helper: register a listener and record it for cleanup. */
		function on(target, type, fn, options) {
			target.addEventListener(type, fn, options || false);
			state.listeners.push({ target: target, type: type, fn: fn, options: options || false });
		}

		// ── Slide navigation helpers ──────────────────────────────────────────

		function getSlideWidth() {
			var firstSlide = slides[0];
			return firstSlide ? firstSlide.getBoundingClientRect().width : 0;
		}

		function getVisibleSlidesCount() {
			var trackContainer = carousel.querySelector('.reviewflow-carousel__track-container');
			if (!trackContainer) { return 1; }
			var slideWidth = getSlideWidth();
			if (slideWidth <= 0) { return 1; }
			return Math.round(trackContainer.getBoundingClientRect().width / slideWidth) || 1;
		}

		function getMaxIndex() {
			return Math.max(0, slides.length - getVisibleSlidesCount());
		}

		function moveToSlide(index) {
			// If any review is expanded, collapse it smoothly so track height resets cleanly
			if (hasExpandedReviews()) {
				collapseAllInCarousel(true);
				isExpandedPaused = false;
			}

			var maxIdx = getMaxIndex();
			if (loop) {
				if (index < 0)           { index = maxIdx; }
				else if (index > maxIdx) { index = 0; }
			} else {
				if (index < 0)           { index = 0; }
				else if (index > maxIdx) { index = maxIdx; }
			}

			var slideWidth = getSlideWidth();
			if (track) {
				track.style.transform = 'translateX(-' + (slideWidth * index) + 'px)';
			}
			currentIndex = index;

			// Handle non-looping button disabled states
			if (!loop) {
				if (prevButton) {
					prevButton.disabled = (currentIndex <= 0);
					prevButton.classList.toggle('reviewflow-carousel__button--disabled', currentIndex <= 0);
				}
				if (nextButton) {
					nextButton.disabled = (currentIndex >= maxIdx);
					nextButton.classList.toggle('reviewflow-carousel__button--disabled', currentIndex >= maxIdx);
				}
			}

			indicators.forEach(function (indicator, idx) {
				if (idx === currentIndex) {
					indicator.classList.add('reviewflow-carousel__indicator--active');
					indicator.setAttribute('aria-selected', 'true');
				} else {
					indicator.classList.remove('reviewflow-carousel__indicator--active');
					indicator.setAttribute('aria-selected', 'false');
				}
			});
		}

		// ── Autoplay ──────────────────────────────────────────────────────────

		function startAutoplay() {
			if (hasReducedMotion || isExpandedPaused || hasExpandedReviews()) { return; }
			if (autoplayTimer) { clearInterval(autoplayTimer); }
			autoplayTimer = setInterval(function () {
				if (isExpandedPaused || hasExpandedReviews()) {
					stopAutoplay();
					return;
				}
				var maxIdx = getMaxIndex();
				if (!loop && currentIndex >= maxIdx) {
					stopAutoplay();
					return;
				}
				moveToSlide(currentIndex + 1);
			}, interval);
		}

		function stopAutoplay() {
			if (autoplayTimer) {
				clearInterval(autoplayTimer);
				autoplayTimer = null;
			}
		}

		// ── Expand / Collapse event synchronization ──────────────────────────

		on(carousel, 'reviewflow:expand', function () {
			isExpandedPaused = true;
			stopAutoplay();
		});

		on(carousel, 'reviewflow:collapse', function () {
			// Check if any other reviews in this carousel are still expanded
			setTimeout(function () {
				if (!hasExpandedReviews()) {
					isExpandedPaused = false;
					if (autoplay) {
						startAutoplay();
					}
				}
			}, 0);
		});

		// Keep stopAutoplay reference current on state (it captures autoplayTimer via closure)
		state.stopAutoplay = stopAutoplay;

		// ── Window resize ─────────────────────────────────────────────────────

		state.windowResizeHandler = function () {
			var maxIdx = getMaxIndex();
			if (currentIndex > maxIdx) { currentIndex = maxIdx; }
			moveToSlide(currentIndex);
		};
		window.addEventListener('resize', state.windowResizeHandler);

		// ── Navigation buttons ────────────────────────────────────────────────

		if (nextButton) {
			on(nextButton, 'click', function () {
				moveToSlide(currentIndex + 1);
				stopAutoplay();
				if (autoplay) { startAutoplay(); }
			});
		}

		if (prevButton) {
			on(prevButton, 'click', function () {
				moveToSlide(currentIndex - 1);
				stopAutoplay();
				if (autoplay) { startAutoplay(); }
			});
		}

		// ── Dot indicators ────────────────────────────────────────────────────

		indicators.forEach(function (indicator) {
			on(indicator, 'click', function (e) {
				var targetIndex = parseInt(e.currentTarget.getAttribute('data-slide-index'), 10);
				moveToSlide(targetIndex);
				stopAutoplay();
				if (autoplay) { startAutoplay(); }
			});
		});

		// ── Keyboard controls ─────────────────────────────────────────────────

		carousel.setAttribute('tabindex', '0');
		on(carousel, 'keydown', function (e) {
			var activeEl = document.activeElement;
			if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.tagName === 'SELECT')) {
				return;
			}
			if (e.key === 'ArrowLeft') {
				moveToSlide(currentIndex - 1);
				stopAutoplay();
				if (autoplay) { startAutoplay(); }
				e.preventDefault();
			} else if (e.key === 'ArrowRight') {
				moveToSlide(currentIndex + 1);
				stopAutoplay();
				if (autoplay) { startAutoplay(); }
				e.preventDefault();
			}
		});

		// ── Touch / swipe & dragging ──────────────────────────────────────────

		if (draggable) {
			carousel.classList.add('reviewflow-carousel--draggable');
			var startX = 0;
			var startY = 0;
			var currentX = 0;
			var currentY = 0;
			var isDragging = false;

			on(carousel, 'touchstart', function (e) {
				if (e.touches && e.touches[0]) {
					startX = e.touches[0].clientX;
					startY = e.touches[0].clientY;
					isDragging = true;
					stopAutoplay();
				}
			}, { passive: true });

			on(carousel, 'touchmove', function (e) {
				if (!isDragging || !e.touches || !e.touches[0]) { return; }
				currentX = e.touches[0].clientX;
				currentY = e.touches[0].clientY;
			}, { passive: true });

			on(carousel, 'touchend', function () {
				if (!isDragging) { return; }
				isDragging = false;
				var diffX = startX - currentX;
				var diffY = startY - currentY;
				if (Math.abs(diffX) > 40 && Math.abs(diffX) > Math.abs(diffY)) {
					moveToSlide(diffX > 0 ? currentIndex + 1 : currentIndex - 1);
				}
				if (autoplay) { startAutoplay(); }
			});

			// Desktop mouse drag
			var isMouseDown = false;
			var mouseStartX = 0;
			var mouseStartY = 0;
			var mouseCurrentX = 0;

			on(carousel, 'mousedown', function (e) {
				if (e.button !== 0) { return; }
				if (e.target.closest('button, a, input, textarea, select')) { return; }
				isMouseDown = true;
				mouseStartX = e.clientX;
				mouseStartY = e.clientY;
				mouseCurrentX = e.clientX;
				carousel.classList.add('reviewflow-carousel--dragging');
				stopAutoplay();
			});

			on(carousel, 'mousemove', function (e) {
				if (!isMouseDown) { return; }
				mouseCurrentX = e.clientX;
			});

			var finishMouseDrag = function () {
				if (!isMouseDown) { return; }
				isMouseDown = false;
				carousel.classList.remove('reviewflow-carousel--dragging');
				var diffX = mouseStartX - mouseCurrentX;
				if (Math.abs(diffX) > 40) {
					moveToSlide(diffX > 0 ? currentIndex + 1 : currentIndex - 1);
				}
				if (autoplay) { startAutoplay(); }
			};

			on(carousel, 'mouseup', finishMouseDrag);
			on(carousel, 'mouseleave', function () {
				if (isMouseDown) {
					finishMouseDrag();
				}
			});
		}

		// ── Pause on hover / focus ────────────────────────────────────────────

		on(carousel, 'mouseenter', stopAutoplay);
		on(carousel, 'mouseleave', function () {
			if (autoplay && !isExpandedPaused && !hasExpandedReviews()) {
				startAutoplay();
			}
		});
		on(carousel, 'focusin', stopAutoplay);
		on(carousel, 'focusout', function (e) {
			if (autoplay && !isExpandedPaused && !hasExpandedReviews() && !carousel.contains(e.relatedTarget)) {
				startAutoplay();
			}
		});

		// ── Init ──────────────────────────────────────────────────────────────

		setTimeout(function () {
			moveToSlide(currentIndex);
			if (autoplay) { startAutoplay(); }
		}, 100);
	}
})();
